// Pure navigation logic for the responsive shell (KSE-0191): which mail pane a
// compact (phone) viewport shows, how the stacked panes map onto browser
// history so the platform back gesture pops them, and the launch query
// parameters the manifest shortcuts use. No DOM here — the hooks in
// AppShell.tsx / MailShell.tsx apply the decisions; this file is unit-tested.

export type Surface = "mail" | "calendar";

export const SURFACE_STORAGE_KEY = "lately.web.surface";
export const NAVIGATE_EVENT = "lately-navigate";
export const HISTORY_STATE_KEY = "latelyStack";

/** Which mail pane is on screen. `split` is the desktop three-pane layout. */
export type MailPane = "split" | "mailboxes" | "threads" | "message";

/**
 * The compact viewport shows one pane at a time. The message pane needs a
 * selected thread in the snapshot AND the user to have opened it (a tap, ⏎,
 * or a notification click): the core keeps a thread selected whenever the
 * list is non-empty (it falls back to the first one, like the desktop
 * reader), so the selection alone cannot say whether the phone is reading
 * or browsing. The drawer wins over an open message so "Mailboxes" works
 * from anywhere.
 */
export function derivePane(input: {
  compact: boolean;
  selectedThreadId: string | null;
  messageOpen: boolean;
  drawerOpen: boolean;
}): MailPane {
  if (!input.compact) {
    return "split";
  }
  if (input.drawerOpen) {
    return "mailboxes";
  }
  if (input.messageOpen && input.selectedThreadId) {
    return "message";
  }
  return "threads";
}

// ---------------------------------------------------------------------------
// History integration

/**
 * One pushed history entry per layer stacked above the root pane. Mail: the
 * composer sits on top of whichever pane it was opened from. Calendar: the
 * full-screen event sheet and the calendars bottom sheet. Desktop pushes
 * nothing (no layer is modal there); each surface records only its own
 * layers (MAIL_LAYERS / CALENDAR_LAYERS), so a hook seeding from
 * `history.state` or landing on a popstate never acts on the other
 * surface's entries — the two never pop each other's stack.
 */
export type HistoryLayer = "mailboxes" | "message" | "composer" | "event" | "calendars";

export const MAIL_LAYERS: readonly HistoryLayer[] = ["mailboxes", "message", "composer"];
export const CALENDAR_LAYERS: readonly HistoryLayer[] = ["event", "calendars"];
const ALL_LAYERS: readonly HistoryLayer[] = [...MAIL_LAYERS, ...CALENDAR_LAYERS];

export function desiredLayers(input: {
  compact: boolean;
  drawerOpen: boolean;
  // derivePane's message condition: opened by the user and selected.
  messageOpen: boolean;
  composerOpen: boolean;
}): HistoryLayer[] {
  if (!input.compact) {
    return [];
  }
  const layers: HistoryLayer[] = [];
  if (input.drawerOpen) {
    layers.push("mailboxes");
  } else if (input.messageOpen) {
    layers.push("message");
  }
  if (input.composerOpen) {
    layers.push("composer");
  }
  return layers;
}

/**
 * The calendar surface's stack on a phone: the event sheet (an occurrence is
 * selected) and the calendars bottom sheet on top of it. Desktop: nothing
 * (the detail is a side panel, the calendars a sidebar).
 */
export function desiredCalendarLayers(input: {
  compact: boolean;
  eventOpen: boolean;
  calendarsOpen: boolean;
}): HistoryLayer[] {
  if (!input.compact) {
    return [];
  }
  const layers: HistoryLayer[] = [];
  if (input.eventOpen) {
    layers.push("event");
  }
  if (input.calendarsOpen) {
    layers.push("calendars");
  }
  return layers;
}

export type HistoryPlan =
  | { kind: "none" }
  | { kind: "push"; layers: HistoryLayer[] }
  | { kind: "back"; count: number }
  | { kind: "replace"; layers: HistoryLayer[] };

function isPrefix(prefix: readonly HistoryLayer[], of: readonly HistoryLayer[]): boolean {
  return prefix.length <= of.length && prefix.every((layer, index) => of[index] === layer);
}

/**
 * How to move the browser history from the layers it currently records to
 * the layers the UI wants: push the new ones, go back over the ones that
 * closed, or (a lateral move, e.g. drawer replaced by message) rewrite the
 * top entry. Never pushes when the state is already there, and never pops
 * past the root entry (the app's own first entry stays put).
 */
export function planHistory(
  current: readonly HistoryLayer[],
  desired: readonly HistoryLayer[],
): HistoryPlan {
  if (current.length === desired.length && isPrefix(current, desired)) {
    return { kind: "none" };
  }
  if (isPrefix(current, desired)) {
    return { kind: "push", layers: desired.slice(current.length) };
  }
  if (isPrefix(desired, current)) {
    return { kind: "back", count: current.length - desired.length };
  }
  return { kind: "replace", layers: [...desired] };
}

/**
 * The layers a `popstate` state object (or `history.state`) records, limited
 * to `owned` (the calling surface's layers; default all) — an entry pushed by
 * the other surface reads as the root to this one.
 */
export function layersFromState(
  state: unknown,
  owned: readonly HistoryLayer[] = ALL_LAYERS,
): HistoryLayer[] {
  if (!state || typeof state !== "object") {
    return [];
  }
  const raw = (state as Record<string, unknown>)[HISTORY_STATE_KEY];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((layer): layer is HistoryLayer => owned.includes(layer as HistoryLayer));
}

/** The state object to push/replace for a stack of layers. */
export function stateForLayers(layers: readonly HistoryLayer[]): Record<string, unknown> {
  return { [HISTORY_STATE_KEY]: [...layers] };
}

/**
 * What the UI must close after the platform back gesture landed on `layers`
 * (the popped entry's state): everything the new top no longer records.
 */
export function popTo(layers: readonly HistoryLayer[]): {
  composerOpen: boolean;
  drawerOpen: boolean;
  messageOpen: boolean;
} {
  return {
    composerOpen: layers.includes("composer"),
    drawerOpen: layers.includes("mailboxes"),
    messageOpen: layers.includes("message"),
  };
}

/** The calendar sheets that stay open after a back gesture landed on `layers`. */
export function popCalendarTo(layers: readonly HistoryLayer[]): {
  eventOpen: boolean;
  calendarsOpen: boolean;
} {
  return {
    eventOpen: layers.includes("event"),
    calendarsOpen: layers.includes("calendars"),
  };
}

// ---------------------------------------------------------------------------
// Surface persistence + launch parameters

export function readStoredSurface(read: (key: string) => string | null): Surface {
  try {
    return read(SURFACE_STORAGE_KEY) === "calendar" ? "calendar" : "mail";
  } catch {
    return "mail";
  }
}

export interface LaunchIntent {
  compose: boolean;
  surface: Surface | null;
}

/**
 * The manifest shortcuts open `/?compose=1` and `/?surface=calendar`; the
 * shell acts on them once at load and strips them from the address bar
 * (`stripLaunchParams`), leaving every other dev knob (`?engine=…`) alone.
 */
export function parseLaunchIntent(search: string): LaunchIntent {
  const params = new URLSearchParams(search);
  const surface = params.get("surface");
  return {
    compose: params.get("compose") === "1",
    surface: surface === "calendar" || surface === "mail" ? surface : null,
  };
}

/** The URL (path + query + hash) with the launch parameters removed. */
export function stripLaunchParams(url: string): string {
  const parsed = new URL(url, "http://lately.invalid");
  parsed.searchParams.delete("compose");
  parsed.searchParams.delete("surface");
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
